import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy · Buddha KA" },
      { name: "description", content: "How Buddha KA and KAfrequency collect, use, and protect your personal information." },
      { property: "og:title", content: "Privacy Policy · Buddha KA" },
      { property: "og:description", content: "How Buddha KA and KAfrequency handle your data." },
      { property: "og:url", content: "https://buddhaka.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://buddhaka.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Legal</p>
      <h1 className="mt-4 font-serif text-5xl">Privacy Policy</h1>
      <p className="mt-3 text-xs text-muted-foreground">Last updated: July 15, 2026</p>

      <div className="prose prose-invert mt-10 max-w-none space-y-6 text-sm leading-relaxed text-muted-foreground">
        <p>Buddha KA and KAfrequency ("we", "us", "our") operate buddhaka.com and its related services. This policy explains what we collect, how we use it, and the rights you have over your information.</p>

        <h2 className="font-serif text-2xl text-foreground">Information we collect</h2>
        <ul className="list-disc space-y-1 pl-6">
          <li><strong>Account information</strong>: name, email, password hash, profile preferences.</li>
          <li><strong>Order information</strong>: products purchased, billing and shipping address, order history, and payment metadata (processed by Stripe — we never store full card numbers).</li>
          <li><strong>Membership data</strong>: subscription tier, status, renewal dates.</li>
          <li><strong>Usage data</strong>: pages visited, features used, device type, IP address, and referral source, collected via cookies and analytics.</li>
          <li><strong>Communications</strong>: messages you send us through contact forms or email.</li>
        </ul>

        <h2 className="font-serif text-2xl text-foreground">How we use your information</h2>
        <ul className="list-disc space-y-1 pl-6">
          <li>Provide and improve the site, music, books, memberships, and shop.</li>
          <li>Process purchases, deliver digital downloads, and ship physical goods.</li>
          <li>Send order confirmations, receipts, shipping updates, membership notices, and password resets.</li>
          <li>Send newsletters and marketing only when you opt in — you can unsubscribe anytime.</li>
          <li>Detect fraud, enforce our Terms, and comply with legal obligations.</li>
        </ul>

        <h2 className="font-serif text-2xl text-foreground">Who we share it with</h2>
        <p>We share the minimum necessary data with service providers who process it on our behalf:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>Supabase</strong> — hosting, authentication, and database.</li>
          <li><strong>Lovable Cloud / email infrastructure</strong> — transactional email delivery.</li>
          <li><strong>Meta (Facebook) Pixel</strong> — advertising performance measurement.</li>
        </ul>
        <p>We do not sell your personal information.</p>

        <h2 className="font-serif text-2xl text-foreground">Data retention</h2>
        <p>Account and order records are retained for as long as your account is active plus the period required by tax, accounting, and consumer-protection law. You can request deletion at any time (see below).</p>

        <h2 className="font-serif text-2xl text-foreground">Your rights</h2>
        <p>Depending on your jurisdiction (including GDPR and CCPA), you may have the right to access, correct, delete, or export your data, and to object to certain processing. To exercise these rights, email <a className="text-gold underline" href="mailto:support@buddhaka.com">support@buddhaka.com</a>.</p>

        <h2 className="font-serif text-2xl text-foreground">Security</h2>
        <p>We use HTTPS, encrypted database connections, hashed passwords, and role-based access controls. No system is perfectly secure — please use a strong, unique password.</p>

        <h2 className="font-serif text-2xl text-foreground">Children</h2>
        <p>The site is not directed to children under 13, and we do not knowingly collect their information.</p>

        <h2 className="font-serif text-2xl text-foreground">Changes</h2>
        <p>We may update this policy from time to time. Material changes will be announced on this page with an updated date.</p>

        <h2 className="font-serif text-2xl text-foreground">Contact</h2>
        <p><a className="text-gold underline" href="mailto:support@buddhaka.com">support@buddhaka.com</a></p>
      </div>
    </div>
  );
}
